// 契约 Vo → 展示判定的纯函数派生层。后端下发事实（状态 + 参数），这里派生展示语义：
// tag、stale、bar 场景、tile 排序、格式化。全部无副作用，可单测。
import {
    ECoverageStatus,
    EEligibilityOutcome,
    EEligibilityStatus,
    ELIGIBILITY_FRESHNESS_WINDOW_DAYS,
    ELIGIBILITY_REJECTION_STATUSES,
    ELIGIBILITY_TAG_BY_BACKEND_STATUS,
    headlineToken,
    ENetworkStatus,
    TCostSharing,
    TEligibilityCheckVo,
    TEligibilityResult,
} from 'types/insuranceEligibility';
import dayjs from 'utils/dayjs';

// ---------- 时间 ----------

export type TFreshness = {
    stale: boolean;
    ageDays: number;
    ageLabel: string;
};

export const freshness = (checkedAt: string): TFreshness => {
    const then = dayjs(checkedAt);
    const now = dayjs();
    const minutes = now.diff(then, 'minute');
    const hours = now.diff(then, 'hour');
    const days = now.diff(then, 'day');
    let ageLabel: string;
    if (minutes < 1) {
        ageLabel = 'just now';
    } else if (minutes < 60) {
        ageLabel = `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
    } else if (hours < 24) {
        ageLabel = `${hours} hour${hours === 1 ? '' : 's'} ago`;
    } else {
        ageLabel = `${days} day${days === 1 ? '' : 's'} ago`;
    }
    return { stale: days >= ELIGIBILITY_FRESHNESS_WINDOW_DAYS, ageDays: days, ageLabel };
};

// "Aug 4"，跨年才带年份
export const formatCheckedDate = (iso: string): string => {
    const d = dayjs(iso);
    return d.year() === dayjs().year() ? d.format('MMM D') : d.format('MMM D, YYYY');
};

// hover 用完整时间戳 "Aug 4, 2026 · 3:12 PM"
export const formatCheckedAtFull = (iso: string): string => dayjs(iso).format('MMM D, YYYY · h:mm A');

// History 表格的日期：永远带年份。这张表跨年，今年的行掉年份会读成两种格式（原型 09/15）
export const formatHistoryDate = (iso: string): string => dayjs(iso).format('MMM D, YYYY');

// plan 期限/失效日期用数字格式 "01/01/2026"（原型 fmtDate；与 Last checked 的 "Aug 4" 是两套）
export const formatPlanDate = (iso: string): string => dayjs(iso).format('MM/DD/YYYY');

// 271 的 payer 名是全大写无空格（"UNITEDHEALTHCARE"），title-case 修不了（找不到词界）。
// 归一化后与档案 carrier 相同时优先用档案拼写（provider 在本屏其他地方看到的就是它）；
// 其余 title-case 兜底；payer 没回名字时 "This payer"（原型 payerProse 逐字移植）
export const payerProse = (name?: string, carrierOnFile?: string): string => {
    if (
        carrierOnFile &&
        name &&
        carrierOnFile.replace(/\s+/g, '').toUpperCase() === name.replace(/\s+/g, '').toUpperCase()
    ) {
        return carrierOnFile;
    }
    if (!name) return 'This payer';
    if (name !== name.toUpperCase()) return name;
    return name.toLowerCase().replace(/\b[a-z]/g, (c) => c.toUpperCase());
};

// ---------- 金额 ----------

export const formatCents = (cents: number): string => {
    const dollars = cents / 100;
    const hasCents = cents % 100 !== 0;
    return `$${dollars.toLocaleString('en-US', {
        minimumFractionDigits: hasCents ? 2 : 0,
        maximumFractionDigits: hasCents ? 2 : 0,
    })}`;
};

// ---------- 结果轴判定 ----------

// 单次费用这一类里 payer 到底报了没有。三处判定（isPartial / priceFirst / InfoLines 的
// unpriced）问的是同一件事，抄第三遍必然漂移
export const anyPriceReported = (cs: TCostSharing): boolean =>
    cs.copay.reported || cs.medicalCopay.reported || cs.coinsurance.reported;

// 「这次问诊一个价格都没拿到」：两个 copay（behavioral + 一般医疗门诊）和 coinsurance 都没报。
// 后端的 NO_PER_VISIT_PRICE / PLANWIDE_ONLY 口径同步改成两个 copay 一起检验，这里跟着对齐——
// 否则 BH 缺、medical 有的时候徽章说有价格、横幅说没有
export const isPartial = (result: TEligibilityResult): boolean =>
    result.coverageStatus === ECoverageStatus.ACTIVE && !!result.costSharing && !anyPriceReported(result.costSharing);

export const isConflicted = (result: TEligibilityResult): boolean => {
    const cs = result.costSharing;
    if (!cs) return false;
    return [cs.copay, cs.coinsurance].some((t) => (t.values?.length || 0) > 1);
};

export const isCarveOut = (result: TEligibilityResult): boolean => !!result.behavioralHealth?.carveOut;

export const isOon = (result: TEligibilityResult): boolean => result.network?.status === ENetworkStatus.OUT_OF_NETWORK;

// tiles 排序：有 copay（任一种）或一个价格都没报时，价格类在前（原型 priceFirst）
export const priceFirst = (cs: TCostSharing): boolean =>
    cs.copay.reported || cs.medicalCopay.reported || !cs.coinsurance.reported;

// ---------- bar 场景（决定 tone + 一句话文案，copy.ts 按场景查表） ----------

export type TBarScene =
    'rejected' | 'inactive' | 'oon' | 'nonCovered' | 'conflict' | 'carve' | 'partial' | 'unknown' | 'active';

// 后端 primary status → bar 场景（statusTokens 存在时优先用；detail 派生只作兜底）
const SCENE_BY_PRIMARY: Record<string, TBarScene> = {
    INACTIVE: 'inactive',
    OUT_OF_NETWORK: 'oon',
    SERVICE_NOT_COVERED: 'nonCovered',
    CONFLICTING_AMOUNTS: 'conflict',
    CARVE_OUT: 'carve',
    // 原型该场景 bar 是绿色 Coverage is active（它的 priced 判定被计划级 coinsurance 满足）。
    // 「没有本次问诊的价格」由 InfoLines 的两句话承担，不占顶栏的色调
    PLANWIDE_ONLY: 'active',
    NO_PER_VISIT_PRICE: 'partial',
    ACTIVE: 'active',
};

export const barScene = (check: TEligibilityCheckVo): TBarScene => {
    const tokens = check.statusTokens || [];
    if (tokens.length) {
        if (tokens.some((t) => ELIGIBILITY_REJECTION_STATUSES.has(t))) return 'rejected';
        // 与 tag 同一个 token（headlineToken），两处永远说的是同一件事
        return SCENE_BY_PRIMARY[headlineToken(tokens) || tokens[0]] || 'unknown';
    }
    if (check.outcome === EEligibilityOutcome.REJECTED || !check.result) return 'rejected';
    const r = check.result;
    if (r.coverageStatus === ECoverageStatus.INACTIVE) return 'inactive';
    if (isOon(r)) return 'oon';
    if (r.serviceNotCovered) return 'nonCovered';
    if (isConflicted(r)) return 'conflict';
    if (isCarveOut(r)) return 'carve';
    if (isPartial(r)) return 'partial';
    if (r.coverageStatus === ECoverageStatus.UNKNOWN) return 'unknown';
    return 'active';
};

// ---------- tag 派生（原型 tagState 优先级） ----------

export const deriveTagFromCheck = (
    check: TEligibilityCheckVo | undefined,
    hasInsurance: boolean,
): EEligibilityStatus => {
    if (!hasInsurance) return EEligibilityStatus.NO_INSURANCE;
    if (!check) return EEligibilityStatus.NEVER_CHECKED;
    // 只报后端给的状态（2026-09-17 拍板）：7 天 stale 不再变成 Needs re-check，否则列表与
    // 这里会对同一个患者给出两种说法。时间态仍在结果卡的 last-checked 行上以琥珀色表达
    // 后端断言的多状态串：按原型 tagState 的顺序挑头条 token，再查词汇表
    const headline = headlineToken(check.statusTokens || []);
    if (headline) return ELIGIBILITY_TAG_BY_BACKEND_STATUS[headline] || EEligibilityStatus.CHECKED;
    if (check.outcome === EEligibilityOutcome.REJECTED || !check.result) {
        return check.outcome === EEligibilityOutcome.REJECTED
            ? EEligibilityStatus.COULD_NOT_VERIFY
            : EEligibilityStatus.CHECKED;
    }
    const r = check.result;
    if (r.coverageStatus === ECoverageStatus.INACTIVE) return EEligibilityStatus.INACTIVE;
    if (isOon(r)) return EEligibilityStatus.OUT_OF_NETWORK;
    if (r.serviceNotCovered) return EEligibilityStatus.SERVICE_NOT_COVERED;
    if (isConflicted(r)) return EEligibilityStatus.CONFLICTING_AMOUNTS;
    if (isPartial(r) || isCarveOut(r)) return EEligibilityStatus.NO_COST_ESTIMATE;
    if (r.coverageStatus === ECoverageStatus.ACTIVE) return EEligibilityStatus.ELIGIBLE;
    return EEligibilityStatus.CHECKED;
};

// 10/05 多份 primary 的成败口径（与后端对齐，2026-10-13）：头条 status 是 ACTIVE / PLANWIDE_ONLY /
// NO_PER_VISIT_PRICE 才算成功，其余（拒识、inactive、OON、SNC、CONFLICTING_AMOUNTS、CARVE_OUT、UNMAPPED、未知）都算失败。
// 头条与 tag 同一套挑法（headlineToken）。失败 → 「查另一张」与要卡文案点名这张；成功且不是 default → Set as default
const SUCCESS_STATUSES = new Set(['ACTIVE', 'PLANWIDE_ONLY', 'NO_PER_VISIT_PRICE']);
export const isEligibleResult = (check: TEligibilityCheckVo): boolean => {
    const headline = headlineToken(check.statusTokens || []);
    if (headline) return SUCCESS_STATUSES.has(headline);
    // 没有 status 串（老数据）：退回 detail 派生的场景
    const scene = barScene(check);
    return scene === 'active' || scene === 'partial';
};

// History 行的 tag：后端 §2.8 下发的已经是单个 primary token，直接查词汇表，不派生 stale。
// 流水回答的是「payer 当时说了什么」，不是「现在还新不新」——三月那次 Inactive 不会因为
// 过了 7 天变成 Needs re-check（原型 answeredLabel）。调用方负责 status 为空时不渲染徽章
export const historyRowTag = (status: string): EEligibilityStatus =>
    ELIGIBILITY_TAG_BY_BACKEND_STATUS[status.trim()] || EEligibilityStatus.CHECKED;
